<template>
  <div class="max-w-6xl mx-auto px-4 sm:px-6">
    <div class="pt-32 pb-10 md:pt-40">
      <div class="text-center">
        <div
          class="inline-flex font-medium bg-clip-text text-transparent bg-[#1ba7ce] dark:bg-gradient-to-r from-green-500 to-green-200 pb-3"
        >
          Last Updated July 30, 2026
        </div>
        <h1
          class="h1 bg-clip-text text-transparent bg-gradient-to-r from-slate-950/60 via-slate-800 to-slate-950/60 dark:from-slate-200/60 dark:via-slate-200 dark:to-slate-200/60 pb-6"
        >
          Terms of Service
        </h1>
      </div>
      <div class="mt-10 rounded-lg border border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-900 px-4 py-3 text-sm text-black dark:text-slate-50">
        This is a previous version of the Terms of Service. It applies until November 16, 2026. After that date the version at
        <a href="/terms-of-service" class="text-[#1ba7ce] underline">workspace369.com/terms-of-service</a> applies.
      </div>
      <div class="mt-20 text-black dark:text-slate-50">
        <h2 class="font-bold text-xl my-10">1. Agreement</h2>
        <p class="my-5">
          These Terms of Service ("Terms") are a legally binding agreement
          between you and Workspace369 LLC ("Workspace369", "we", "us", "our"),
          a Florida limited liability company with its registered office at 8225
          Emerald Winds Circle, Boynton Beach, FL 33473, USA. They govern your
          access to and use of the Workspace369 client-work operating system —
          including the website at workspace369.com, the applications at
          app.workspace369.com, and related mobile and desktop applications
          (collectively, the "Service"). By creating an account, accessing, or
          using the Service, you agree to these Terms and our Privacy Policy. If
          you use the Service on behalf of a company or other entity, you
          represent that you have authority to bind that entity, and "you"
          refers to that entity. If you do not agree, do not use the Service.
        </p>

        <h2 class="font-bold text-xl my-10">2. The Service</h2>
        <p class="my-5">
          The Service provides customer relationship management, projects and
          tasks, proposals, invoicing and payments, scheduling, a shared inbox
          with email, SMS, voice and voicemail, AI-assisted summaries and
          automations, client portals, files, expenses, accounting, reporting,
          and integrations with third-party services. We may add, change, or
          remove features over time; we will not materially reduce the core
          functionality of a paid plan during a paid term without notice.
        </p>

        <h2 class="font-bold text-xl my-10">3. Eligibility and accounts</h2>
        <p class="my-5">
          You must be at least 18 years old and able to form a binding contract
          to use the Service. You are responsible for the accuracy of your
          registration information, for safeguarding your credentials, and for
          all activity under your account, including by workspace members and
          guests you invite. Workspace owners control member roles and client
          portal access and are responsible for configuring them appropriately.
          Notify us promptly at
          <a href="mailto:support@workspace369.com" class="text-[#1ba7ce] underline"
            >support@workspace369.com</a
          >
          of any unauthorized use.
        </p>

        <h2 class="font-bold text-xl my-10">4. Subscriptions, billing, and trials</h2>
        <p class="my-5">
          Paid plans are billed in advance on a recurring basis (monthly or as
          otherwise stated at checkout) and renew automatically until canceled.
          Free trials, where offered, convert to paid subscriptions at the end
          of the trial unless canceled before the trial ends; trial payment
          methods may be collected at sign-up and charged on conversion. You can
          cancel at any time in the Service; cancellation takes effect at the
          end of the current paid term, and the Service remains available until
          then. Fees are non-refundable except where required by law or
          expressly stated otherwise. Prices exclude taxes; you are responsible
          for applicable taxes, duties, and similar charges. We may change
          prices with at least 30 days' notice, effective on your next renewal.
          Unpaid amounts may result in suspension of the Service.
        </p>

        <h2 class="font-bold text-xl my-10">5. Payments, Stripe, and Stripe Connect</h2>
        <p class="my-5">
          <strong>Processor.</strong> Payments for the Service are processed by
          Stripe, Inc. ("Stripe"). By paying for the Service you agree to be
          bound by the
          <a
            href="https://stripe.com/legal"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[#1ba7ce] underline"
            >Stripe Services Agreement</a
          >. We do not store your full payment card number.
        </p>
        <p class="my-5">
          <strong>Accepting payments (Stripe Connect).</strong> The Service lets
          you accept payments from your own clients by connecting a Stripe
          account. By connecting an account, you agree to the Stripe Connected
          Account Agreement and the Stripe Services Agreement, and you
          acknowledge that payment processing for those transactions is provided
          by Stripe, not Workspace369. You are the merchant of record for your
          sales: you are solely responsible for your products and services,
          refunds, chargebacks, disputes, taxes, and compliance with card
          network rules and applicable law. Workspace369 is not a bank, money
          transmitter, or payment institution, does not hold your funds, and is
          not a party to transactions between you and your clients.
        </p>
        <p class="my-5">
          <strong>Application fee.</strong> For each transaction processed
          through your connected Stripe account via the Service, we charge an
          application fee equal to one percent (1%) of the transaction amount.
          The application fee is deducted automatically from each transaction
          before settlement and is in addition to Stripe's processing fees,
          which are set by and payable to Stripe under your Stripe agreement.
          Application fees are non-refundable, including in the event of a
          refund or chargeback of the underlying transaction to the extent
          permitted by card network rules.
        </p>
        <p class="my-5">
          <strong>Payouts and reserves.</strong> Settlement timing, reserves,
          and payout schedules are determined by Stripe under your Stripe
          agreement. We may suspend payment features if Stripe suspends or
          terminates your connected account or if required for fraud, security,
          or legal reasons.
        </p>

        <h2 class="font-bold text-xl my-10">6. Customer Data</h2>
        <p class="my-5">
          "Customer Data" means the content you and your workspace members
          create, upload, or process in the Service, including client records,
          files, invoices, and the content of communications handled through the
          Service. You retain all rights in your Customer Data. You grant us a
          worldwide, non-exclusive, royalty-free license to host, copy, process,
          transmit, and display Customer Data solely as needed to provide,
          secure, and improve the Service and to comply with law. You represent
          and warrant that you have all rights, permissions, and consents
          necessary to submit Customer Data — including personal data of your
          clients and contacts — to the Service and to process it as you do.
          You are responsible for backing up Customer Data you need; we provide
          export tools and will make your data available for export for a
          reasonable period after termination.
        </p>

        <h2 class="font-bold text-xl my-10">7. Communications compliance</h2>
        <p class="my-5">
          The Service enables email, SMS/MMS, and voice communications, and may
          record and transcribe calls and voicemail. You are solely responsible
          for complying with all laws applicable to your communications,
          including the Telephone Consumer Protection Act (TCPA), the
          CAN-SPAM Act, carrier and 10DLC registration requirements, and
          call-recording consent laws (including two-party-consent states and
          equivalent foreign laws). You must obtain all consents required from
          your contacts before messaging or recording them, honor opt-outs
          promptly, and provide any required disclosures. We may suspend
          messaging features for excessive complaints, carrier flags, or
          suspected unlawful use.
        </p>

        <h2 class="font-bold text-xl my-10">8. AI features</h2>
        <p class="my-5">
          AI-assisted features (such as summaries, drafts, and workflow
          suggestions) are assistive tools. Their output may be inaccurate,
          incomplete, or inappropriate for your context, and does not constitute
          professional, legal, financial, or tax advice. You are responsible for
          reviewing AI output before relying on it or sending it to clients.
        </p>

        <h2 class="font-bold text-xl my-10">9. Third-party services and Google integrations</h2>
        <p class="my-5">
          The Service integrates with third-party services you choose to
          connect, including Google (Calendar, Meet, and, when enabled, Gmail)
          and Stripe. Your use of those services is governed by their own terms,
          including the Google API Terms of Service and the Google API Services
          User Data Policy. We are not responsible for third-party services,
          their availability, or their acts or omissions. You may disconnect any
          integration at any time; disconnecting stops related features and
          future data access.
        </p>

        <h2 class="font-bold text-xl my-10">10. Acceptable use</h2>
        <p class="my-5">You will not, and will not permit anyone to:</p>
        <ul class="list-disc pl-6 space-y-3 my-5">
          <li>use the Service for unlawful, fraudulent, deceptive, or harmful purposes, or to send spam or unlawful messages;</li>
          <li>upload content that is illegal, infringing, defamatory, or malicious, or that contains malware;</li>
          <li>probe, scan, or test the vulnerability of the Service, interfere with its operation, or access it by means other than our supported interfaces;</li>
          <li>submit protected health information, cardholder data (outside Stripe fields), government-classified information, or data subject to special regulation unless we have agreed in writing;</li>
          <li>resell, sublicense, or provide the Service to third parties as a service bureau without our written consent;</li>
          <li>misrepresent your identity, or use the Service in violation of export control or sanctions laws.</li>
        </ul>

        <h2 class="font-bold text-xl my-10">11. Intellectual property</h2>
        <p class="my-5">
          We own the Service, including its software, design, trademarks, and
          documentation, and all rights not expressly granted to you. These
          Terms give you a limited, non-exclusive, non-transferable, revocable
          right to access and use the Service during your subscription for your
          internal business purposes. You own your Customer Data. If you give us
          feedback or suggestions, we may use them without restriction or
          compensation.
        </p>

        <h2 class="font-bold text-xl my-10">12. Confidentiality</h2>
        <p class="my-5">
          Each party may receive non-public information of the other
          ("Confidential Information"). The receiving party will protect it with
          at least reasonable care and use it only to perform under these Terms.
          These obligations do not apply to information that is public without
          breach, independently developed, rightfully received from a third
          party, or required to be disclosed by law (with notice where
          permitted).
        </p>

        <h2 class="font-bold text-xl my-10">13. Term, suspension, and termination</h2>
        <p class="my-5">
          These Terms apply while you use the Service. We may suspend or
          terminate access immediately for material breach, unlawful use, fraud,
          non-payment, or risk to the Service or others, and we will provide
          notice where practicable. You may terminate at any time by canceling
          your subscription and deleting your account. On termination: your
          right to access ends; fees already paid are not refunded except as
          required by law; and we will retain Customer Data for a reasonable
          export period before deletion, as described in our Privacy Policy.
          Sections that by their nature should survive (including Customer Data
          licenses for operation, payment obligations, disclaimers, limitations
          of liability, indemnities, and dispute terms) survive termination.
        </p>

        <h2 class="font-bold text-xl my-10">14. Disclaimers</h2>
        <p class="my-5">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, THE SERVICE IS PROVIDED "AS
          IS" AND "AS AVAILABLE", WITH ALL FAULTS AND WITHOUT WARRANTY OF ANY
          KIND. WE DISCLAIM ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
          NON-INFRINGEMENT, AND ANY WARRANTY ARISING FROM COURSE OF DEALING OR
          USAGE OF TRADE. WE DO NOT WARRANT THAT THE SERVICE WILL BE
          UNINTERRUPTED, ERROR-FREE, OR SECURE, OR THAT AI OUTPUT OR
          COMMUNICATION DELIVERY WILL BE ACCURATE OR TIMELY.
        </p>

        <h2 class="font-bold text-xl my-10">15. Limitation of liability</h2>
        <p class="my-5">
          TO THE MAXIMUM EXTENT PERMITTED BY LAW: (A) NEITHER PARTY WILL BE
          LIABLE FOR INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR
          PUNITIVE DAMAGES, OR FOR LOST PROFITS, REVENUE, DATA, GOODWILL, OR
          BUSINESS OPPORTUNITY, EVEN IF ADVISED OF THEIR POSSIBILITY; AND (B)
          WORKSPACE369'S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATING
          TO THE SERVICE OR THESE TERMS WILL NOT EXCEED THE AMOUNTS YOU PAID US
          FOR THE SERVICE IN THE TWELVE (12) MONTHS BEFORE THE EVENT GIVING
          RISE TO LIABILITY. THESE LIMITS DO NOT APPLY TO LIABILITY THAT CANNOT
          BE LIMITED BY LAW, TO YOUR PAYMENT OBLIGATIONS, OR TO YOUR BREACH OF
          THE ACCEPTABLE USE OR COMMUNICATIONS COMPLIANCE SECTIONS.
        </p>

        <h2 class="font-bold text-xl my-10">16. Indemnification</h2>
        <p class="my-5">
          You will defend, indemnify, and hold harmless Workspace369 and its
          officers, directors, employees, and agents from and against any
          third-party claims, damages, losses, and expenses (including
          reasonable attorneys' fees) arising out of or relating to: your
          Customer Data; your products, services, and transactions with your
          clients (including refunds, chargebacks, and tax obligations); your
          communications through the Service (including alleged TCPA,
          messaging-consent, or call-recording violations); or your breach of
          these Terms or applicable law. We will promptly notify you of any
          claim, give you reasonable cooperation, and allow you to control the
          defense, subject to our right to participate with counsel of our
          choice.
        </p>

        <h2 class="font-bold text-xl my-10">17. Governing law and disputes</h2>
        <p class="my-5">
          These Terms are governed by the laws of the State of Florida, USA,
          without regard to conflict-of-laws rules. Except for claims that
          qualify for small-claims court and claims for injunctive relief
          relating to intellectual property or misuse of the Service, any
          dispute arising out of or relating to these Terms or the Service will
          be resolved by final and binding arbitration administered by the
          American Arbitration Association under its Commercial Arbitration
          Rules, seated in Palm Beach County, Florida, in English, before a
          single arbitrator. YOU AND WORKSPACE369 AGREE THAT DISPUTES WILL BE
          BROUGHT ONLY IN AN INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR
          CLASS MEMBER IN ANY CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION.
          You may opt out of arbitration by emailing
          <a href="mailto:support@workspace369.com" class="text-[#1ba7ce] underline"
            >support@workspace369.com</a
          >
          within 30 days of first accepting these Terms. If you opt out, or if
          this arbitration provision is found unenforceable, disputes will be
          brought exclusively in the state or federal courts located in Palm
          Beach County, Florida, and the parties consent to their jurisdiction.
          Where applicable law gives you a non-waivable right to bring claims in
          your local courts, this section applies only to the extent permitted
          by that law.
        </p>

        <h2 class="font-bold text-xl my-10">18. International use and export controls</h2>
        <p class="my-5">
          The Service is offered worldwide from the United States. You are
          responsible for complying with local laws applicable to your use,
          including data protection, communications, tax, and invoicing rules in
          your jurisdiction. You represent that you are not located in, organized
          in, or a resident of any country or region subject to comprehensive
          US sanctions, and that you are not on any US government restricted
          party list. You will not use, export, or re-export the Service in
          violation of US export control or sanctions laws.
        </p>

        <h2 class="font-bold text-xl my-10">19. General</h2>
        <p class="my-5">
          We may update these Terms from time to time; we will post the updated
          version with a new "Last Updated" date and, for material changes,
          provide additional notice. Continued use after the effective date
          constitutes acceptance. If any provision is unenforceable, the rest
          remain in effect. Our failure to enforce a provision is not a waiver.
          You may not assign these Terms without our consent; we may assign them
          in connection with a merger, acquisition, or sale of assets. These
          Terms, together with the Privacy Policy and any order or plan terms,
          are the entire agreement between you and Workspace369 regarding the
          Service and supersede prior discussions.
        </p>

        <h2 class="font-bold text-xl my-10">20. Contact</h2>
        <p class="my-5">
          Workspace369 LLC, 8225 Emerald Winds Circle, Boynton Beach, FL 33473,
          USA. Legal notices and questions:
          <a href="mailto:support@workspace369.com" class="text-[#1ba7ce] underline"
            >support@workspace369.com</a
          >.
        </p>
      </div>
    </div>
  </div>
</template>

<script setup>
useHead({
  title: 'Terms of Service | Workspace369',
  meta: [
    { name: 'robots', content: 'noindex, follow' },
    {
      name: 'description',
      content:
        'Review Workspace369 terms for account access, subscriptions, payments and Stripe Connect, acceptable use, privacy, and platform responsibilities.'
    },
    { name: 'twitter:title', content: 'Terms of Service | Workspace369' },
    {
      name: 'twitter:description',
      content:
        'Review Workspace369 terms for account access, subscriptions, payments and Stripe Connect, acceptable use, privacy, and platform responsibilities.'
    },
    { name: 'twitter:card', content: 'summary_large_image' },
    { property: 'og:title', content: 'Terms of Service | Workspace369' },
    {
      property: 'og:description',
      content:
        'Review Workspace369 terms for account access, subscriptions, payments and Stripe Connect, acceptable use, privacy, and platform responsibilities.'
    }
  ]
});
</script>
